import {
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { Subject } from 'rxjs';
import { type HubFilters, hubSortButtonLabel, type HubSortField } from '../hub-sort';
import { EntityHubSortMenuComponent } from './entity-hub-sort-menu.component';

@Component({
  selector: 'app-entity-hub-sort',
  standalone: true,
  templateUrl: './entity-hub-sort.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, PopoverDirective, EntityHubSortMenuComponent],
  host: {
    class: 'block',
  },
})
export class EntityHubSortComponent {
  public readonly value = input.required<HubSortField>();
  public readonly filters = input.required<HubFilters>();

  public readonly valueChange = output<HubSortField>();
  public readonly filtersChange = output<HubFilters>();

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  protected readonly menuOpen = signal(false);
  protected readonly menuClose = new Subject<void>();

  protected readonly label = computed(() => hubSortButtonLabel(this.value(), this.filters()));

  /**
   * The popover reports itself hidden before it removes the menu, so whether the focus went down with the
   * menu is only known once the menu is gone.
   */
  protected handleMenuVisibleChange(open: boolean): void {
    const closing = this.menuOpen() && !open;
    this.menuOpen.set(open);

    if (!closing) {
      return;
    }

    const trigger = this.trigger().nativeElement;
    queueMicrotask(() => {
      const active = trigger.ownerDocument.activeElement;
      if (active === null || active === trigger.ownerDocument.body) {
        trigger.focus();
      }
    });
  }

  /** The popover only closes itself on an outside click, so a menu pick has to say so. */
  protected handleSelect(sort: HubSortField): void {
    this.menuClose.next();
    this.valueChange.emit(sort);
  }

  /** A filter is a checkbox, not a pick: the menu stays open so the other one can be toggled too. */
  protected handleFiltersChange(filters: HubFilters): void {
    this.filtersChange.emit(filters);
  }

  /** Tab itself is left to the browser, which then moves on from the trigger. */
  protected handleMenuTabOut(): void {
    this.menuClose.next();
    this.trigger().nativeElement.focus();
  }
}
